import { CONVERSATION_SERVICE_TIMEOUT_MS } from '@gumtree/shared/src/constants/message-centre';
import type { L1Category } from '@gumtree/shared/src/types/client-data';

import { VipState } from '../../../reducers';
import { stickyContactEmailDQ, fixedContactEmailDQ } from '../../contact-buttons/contact-buttons';

export const advertIdStorageKey = 'adId';
export const messageStorageKey = 'textContent';
export const phoneStorageKey = 'phoneNumberContent';

export const phoneRegex = /^[0-9+-\s]*$/;

export function getPlaceholderText(
    isMobile: boolean,
    seoName: L1Category | undefined,
    sellerName: string
) {
    if (isMobile) {
        switch (seoName) {
            case 'cars-vans-motorbikes':
                return "Hi, is this still available? I'm interested! thanks!";
            case 'for-sale':
            case 'pets':
                return 'Hi, is this still available? Thanks!';
        }
    }
    return `Hi ${sellerName},

I'm interested in your item. Is this still available?

Thanks`;
}

export const selectStaticData = ({
    adDetails,
    sellerContactDetails,
    sellerActions,
    baseConfig,
}: VipState) => ({
    advertId: `${adDetails.id}`,
    token: adDetails.token,
    proAccount: adDetails.pro,
    seoName: adDetails.l1CategorySeoName,
    isMotors: adDetails.l1CategorySeoName === 'cars-vans-motorbikes',
    sellerName: sellerContactDetails.contactName,
    sendReplyUrl: sellerActions.sendReplyUrl,
    isMobile: baseConfig.device?.type === 'mobile',
    makeAnOfferEnabled: sellerActions.makeAnOfferEnabled,
    hasPhone: !!sellerContactDetails.replyPhone,
    isCurrentUserSeller: adDetails.isCurrentUserSeller,
    isFromMotorsFeed: adDetails.isFromMotorsFeed,
    replyConfirmationTracking: {
        id: adDetails.id,
        isTrade: adDetails.sellerType === 'Trade',
        subcategory1: adDetails.l1Category,
        subcategory2: adDetails.l2CategoryId,
    },
});

export const smoothScrollFromSticky = (scrollToEl: HTMLFormElement) => {
    const callback = () => scrollToEl.scrollIntoView({ behavior: 'smooth' });
    const scrollFromEls = document.querySelectorAll<HTMLLinkElement>(
        `[data-q="sticky-banner"] [data-q="${stickyContactEmailDQ}"],
     [data-q="${fixedContactEmailDQ}"]
    `
    );
    scrollFromEls.forEach((el) => el.addEventListener('click', callback));
    return () => scrollFromEls.forEach((el) => el.removeEventListener('click', callback));
};

export const sendMessage = async (opts: {
    sendReplyUrl: string;
    advertId: string;
    message: string;
    phoneNumber?: string;
    token: string;
}) => {
    const resp = (await Promise.race([
        new Promise((_, reject) =>
            setTimeout(() => reject('Response timed out'), CONVERSATION_SERVICE_TIMEOUT_MS)
        ),
        fetch(opts.sendReplyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                X_CSRF_TOKEN: opts.token,
            },
            body: JSON.stringify({
                advertId: opts.advertId,
                message: opts.message,
                phoneNumber: opts.phoneNumber,
            }),
        }),
    ])) as Response;

    const payload = await resp.json();
    if (payload.errors) {
        throw { errors: payload.errors.slice(0, 1), payload };
    } else if (!payload.replyConfirmationUrl) {
        throw { payload };
    }

    return payload as {
        replyConfirmationUrl: string;
        errors: null;
    };
};
